/**
 * Einstellungen aufloesen — global und je Projekt.
 *
 * Die Spec verlangt an mehreren Stellen zentral gepflegte, aber
 * ueberschreibbare Werte (PV-F02, PV-J07, PV-J08, PV-K09). Genau das macht
 * dieses Modul, in einer festen Reihenfolge:
 *
 *   Projekteinstellung  ->  globale Einstellung  ->  Notfallwert im Code
 *
 * Der Notfallwert ist bewusst nicht der "richtige" Wert, sondern der, mit dem
 * sich weiterarbeiten laesst, bis Wania den echten eingetragen hat. Wo es
 * keinen vertretbaren Notfallwert gibt — Strompreis, Stundensatz — steht dort
 * null, und die Oberflaeche sagt, dass etwas fehlt.
 */

import type { Einstellung, Projekteinstellung } from './typen.ts';
import type { Pruefvorlage } from '@pv/geometrie';

export interface Einstellungsquelle {
  global: Einstellung[];
  jeProjekt: Projekteinstellung[];
}

export type Herkunft = 'projekt' | 'global' | 'vorgabe';

export interface AufgeloesterWert<T> {
  wert: T;
  herkunft: Herkunft;
}

/**
 * Einen Schluessel aufloesen. Objekte werden flach zusammengefuehrt, damit eine
 * Projekteinstellung nur das ueberschreiben muss, was sie wirklich aendert —
 * wer nur den Stundensatz anpasst, verliert nicht die Aufschlaege.
 */
export function loeseAuf<T>(
  quelle: Einstellungsquelle,
  schluessel: string,
  vorgabe: T,
): AufgeloesterWert<T> {
  const global = quelle.global.find((e) => e.schluessel === schluessel)?.wert;
  const projekt = quelle.jeProjekt.find(
    (e) => e.schluessel === schluessel && !e.geloescht_am,
  )?.wert;

  if (projekt === undefined && global === undefined) return { wert: vorgabe, herkunft: 'vorgabe' };

  if (istObjekt(vorgabe) && (istObjekt(global) || istObjekt(projekt))) {
    return {
      wert: {
        ...(vorgabe as object),
        ...(istObjekt(global) ? global : {}),
        ...(istObjekt(projekt) ? projekt : {}),
      } as T,
      herkunft: projekt !== undefined ? 'projekt' : 'global',
    };
  }

  if (projekt !== undefined) return { wert: projekt as T, herkunft: 'projekt' };
  return { wert: global as T, herkunft: 'global' };
}

/** Nur der Wert, ohne Herkunft — fuer die Rechenwege. */
export const wertVon = <T>(quelle: Einstellungsquelle, schluessel: string, vorgabe: T): T =>
  loeseAuf(quelle, schluessel, vorgabe).wert;

const istObjekt = (wert: unknown): wert is Record<string, unknown> =>
  typeof wert === 'object' && wert !== null && !Array.isArray(wert);

/**
 * Die Notfallwerte, mit denen das Programm anlaeuft, bevor der Server das erste
 * Mal erreicht wurde. Alles, was einen Preis darstellt, steht bewusst auf null.
 */
export const VORGABEN = {
  'raster.standard': {
    randTraufeMm: 300,
    randFirstMm: 300,
    randOrtgangMm: 300,
    fugeQuerMm: 20,
    fugeLaengsMm: 20,
    sicherheitsabstandMm: 300,
    // Auf der ganzen Fläche möglichst mittig (Mathias, 11/2026).
    feldlage: { quer: 'mitte', laengs: 'mitte' },
  },
  'auslegung.temperatur': { kaltGradC: -10, warmGradC: 70, referenzGradC: 25 },
  'flachdach.sonnenstand': { sonnenstandGrad: 17, aufstaenderungGrad: 15 },
  'kalkulation.standard': {
    kabelzuschlagProzent: 15,
    stundenJeModul: 0.35,
    grundstunden: 8,
    stundensatz: null as number | null,
    aufschlaege: {} as Record<string, number>,
  },
  'wirtschaftlichkeit.preise': {
    strompreisJeKwh: null as number | null,
    einspeiseverguetungJeKwh: null as number | null,
    preissteigerungProzent: 3,
    degradationProzent: 0.5,
    betriebskostenJeJahr: null as number | null,
    jahre: 20,
  },
  'wirtschaftlichkeit.anlage': {
    anlagengueteProzent: 84,
    speicherpreisJeKwh: null as number | null,
    speichergroessen: [5, 10, 15, 20],
  },
  'offline.vorladen': { umkreisM: 200, maxZoom: 19, minZoom: 15, hinweisAbTagen: 7 },
  'angebot.vorgaben': {
    gueltigkeitTage: 30,
    hinweis: 'Preise freibleibend. Die Ertragsprognose ist eine Abschätzung, keine Zusage.',
  },
  'preise.ueberschreibungen': {} as Record<string, number>,
  'material.leitungsartikel': {} as Partial<
    Record<
      | 'acKabel'
      | 'rohrAlu'
      | 'rohrEdelstahl'
      | 'dcGeschirmt'
      | 'dcErdkabel'
      | 'acErdkabel'
      | 'netzwerkkabel'
      | 'steuerleitung',
      string
    >
  >,
  /**
   * Prüfpunkte für Besichtigen und Erproben (Mathias, 11/2026). Ein Vorschlag
   * nach E 8101 / IEC 62446-1 — gegen das eigene Prüfprotokoll abgleichen.
   * Dieselbe Liste steht in Migration 0026.
   */
  'pruefbefund.vorlage': {
    abschnitte: [
      {
        titel: 'Besichtigung Gleichstromseite',
        punkte: [
          'Module, Leitungen und Steckverbinder für die höchste Gleichspannung und den höchsten Strom geeignet',
          'Steckverbinder gleichen Typs und Herstellers gepaart',
          'DC-Leitungen gegen UV, Witterung und mechanische Beschädigung geschützt verlegt',
          'Plus- und Minusleitung nebeneinander verlegt, Leiterschleifen klein',
          'DC-Trennstelle vorhanden und gekennzeichnet',
          'Überspannungsschutz DC gesetzt, wo erforderlich',
        ],
      },
      {
        titel: 'Besichtigung Wechselstromseite',
        punkte: [
          'Wechselrichter allpolig freischaltbar (AC-Trennstelle)',
          'Leitungsschutz und Fehlerstromschutz nach Angabe des Wechselrichterherstellers',
          'Überspannungsschutz AC gesetzt, wo erforderlich',
          'Ländereinstellung und Netz- und Anlagenschutz am Wechselrichter eingestellt',
          'Wirkleistungsbegrenzung eingestellt, wo der Netzbetreiber sie vorschreibt',
        ],
      },
      {
        titel: 'Erdung und Potentialausgleich',
        punkte: [
          'Montagegestell in den Potentialausgleich einbezogen',
          'Trennungsabstand zur Blitzschutzanlage eingehalten oder Anlage eingebunden',
        ],
      },
      {
        titel: 'Kennzeichnung und Unterlagen',
        punkte: [
          'Hinweisschild PV-Anlage am Hausanschluss und am Zählerplatz',
          'Übersichtsschaltplan und Stringplan vor Ort',
          'Abschaltanleitung beim Wechselrichter',
        ],
      },
      {
        titel: 'Mechanik',
        punkte: [
          'Montagesystem fest, Dachhaken auf den Trägern, Eindeckung dicht',
          'Leitungsdurchführungen abgedichtet',
          'Wechselrichter ausreichend belüftet',
        ],
      },
      {
        titel: 'Erproben',
        punkte: [
          'Schalter und Trennstellen funktionieren',
          'Fehlerstromschutzschalter löst aus',
          'Wechselrichter schaltet bei Netzausfall ab und startet wieder',
        ],
      },
    ],
  } as Pruefvorlage,
  /** Tage bis zur Erinnerung an einen offenen Schritt (Migration 0027). */
  'abwicklung.erinnerung': {
    netzzugangsvertragTage: 7,
    stromliefervertragTage: 14,
    foerderansuchenTage: 7,
    fertigmeldungTage: 7,
  },
  'material.stringleitung': {
    solarleitungArtikelId: null as string | null,
    steckerpaarArtikelId: null as string | null,
    anschlussleitungM: 1.2,
    zuleitungM: 15,
  },
} as const;
